import { LegalClient } from '~/shared/api/ke-gateway/legal/LegalClient';
import { useLocalStorage } from '@vueuse/core';
import { ELocalStorageKeys } from '~/features/store/ELocalStorageKeys';
import type { IAddLegalCompanyRequest } from '~/shared/api/ke-gateway/legal/model';
import { useLocalStorageNoChanges } from '~/shared/helpers/useLocalStorageNoChanges';

export class LegalSDK {
  private authSDKController: any;
  private legalApi: LegalClient;
  
  private activeLegalCustomerId = useLocalStorageNoChanges(
    ELocalStorageKeys.MM__AUTH_SDK__LEGAL_ACTIVE_ID,
    null
  );
  private legalToken = useLocalStorageNoChanges(ELocalStorageKeys.MM__AUTH_SDK__LEGAL_ACCESS_TOKEN, null);
  private legalRefreshToken = useLocalStorageNoChanges(
    ELocalStorageKeys.MM__AUTH_SDK__LEGAL_REFRESH_TOKEN,
    null
  );
  private legalTokenExpire = useLocalStorageNoChanges(
    ELocalStorageKeys.MM__AUTH_SDK__LEGAL_TOKEN_EXPIRE,
    null
  )

  private defaultState = {
    legal: {
      isLegalCustomersActive: false,
      allLegalCustomers: [] as any[],
      legalCustomerInfo: {
        legalCustomerId: null as any,
        accountId: '',
        phoneNumber: '',
        email: '',
        username: '',
        firstName: '',
        organizationName: '',
        address: '',
        inn: '',
        kpp: '',
        authorities: [] as any[],
        permissions: 0,
      },
      isShownLoggedLegalPopover: false,
      legalCustomerFinance: {
        balance: 0,
        customerId: 0,
        id: '',
        inn: '',
        number: 0,
        status: '',
        type: '',
      },
      legalCustomerEdo: null as any,
    },
  };

  private legacyStateStore = useLocalStorageNoChanges<typeof this.defaultState>(
    ELocalStorageKeys.STATE,
    this.defaultState
  );

  constructor(authSDKController: any) {
    this.authSDKController = authSDKController;
    this.legalApi = new LegalClient(authSDKController);
  }

  async init(isAuthorized: boolean) {
    if (!isAuthorized) return;
    await this.getLegalCustomers();
  }

  async onAuth() {
    await this.getLegalCustomers();
  }

  onLogout() {
    this.logoutAsLegal();
  }

  async getLegalCustomers() {
    const response = await this.legalApi.getLegalCustomers();
    this.legacyStateStore.value.legal.allLegalCustomers = response;
    return response;
  }

  async loginAsLegal(legalId?: number) {
    if (!legalId || typeof legalId !== 'number') {
      legalId = this.legacyStateStore.value.legal.allLegalCustomers[0].legalCustomerId;
    }

    try {
      const response = await this.legalApi.loginAsLegal(legalId);
      this.legalToken.value = response.accessToken;
      this.legalRefreshToken.value = response.refreshToken;
      this.activeLegalCustomerId.value = legalId.toString();
      const expireTime = Date.now() + 15 * 60 * 1000;
      this.legalTokenExpire.value = expireTime.toString();

      await this.getLegalCustomerInfo(legalId);
    } catch (error) {
      console.error(error);
      this.legalToken.value = null;
      this.legalRefreshToken.value = null;
      this.activeLegalCustomerId.value = null;
      this.legalTokenExpire.value = null;
    }
  }

  async getLegalCustomerInfo(legalId: number) {
    try {
      const response = await this.legalApi.getLegalCustomerInfo(legalId);
      this.legacyStateStore.value.legal.legalCustomerInfo = response;
      this.legacyStateStore.value.legal.isLegalCustomersActive = true;
    } catch (error) {
      console.error(error);
      this.legacyStateStore.value.legal.isLegalCustomersActive = false;
      this.legacyStateStore.value.legal.legalCustomerInfo = null;
    }
  }

  async addCompany(request: IAddLegalCompanyRequest) {
    const response = await this.legalApi.addLegalCompany(request);
    await this.getLegalCustomers();

    const expireTime = Date.now() + 15 * 60 * 1000;
    this.legalTokenExpire.value = expireTime.toString();
    this.legalToken.value = response.payload.token.accessToken;
    this.legalRefreshToken.value = response.payload.token.refreshToken;
    this.activeLegalCustomerId.value = response.payload.legalCustomerInfo.legalCustomerId.toString();

    
    await this.getLegalCustomerInfo(response.payload.legalCustomerInfo.legalCustomerId);
  }

  logoutAsLegal() {
    this.legalToken.value = null;
    this.legalRefreshToken.value = null;
    this.activeLegalCustomerId.value = null;
    this.legalTokenExpire.value = null;

    this.legacyStateStore.value.legal = this.defaultState.legal;
  }

  get isLegalActive() {
    return this.activeLegalCustomerId.value !== null;
  }

  get legalCustomerInfo() {
    return this.legacyStateStore.value.legal.legalCustomerInfo;
  }

  get allLegalCustomers() {
    return this.legacyStateStore.value.legal.allLegalCustomers;
  }
}